<template>
	<Dialog :visible="true" style="width: 55vw" modal :closable="false">
		<template #header>
			<div class="flex justify-between w-full">
				<h1>{{ pageEditText }}</h1>
				<div class="flex gap-2">
					<Button id="btnBackToPRList" class="btn-tiny" @click="emit('cancel')">Cancel</Button>
					<Button id="btnSaveProject" class="btn-tiny" v-show="SessionInfo.hasRightsToScreen('Projects', PageAccess.Create)" @click="save()">
						Save
					</Button>
				</div>
			</div>
		</template>
		<!-- Utilities Sharing Flag - Left aligned at top of form -->
		<div v-if="multiStepUtilitiesEnabled" class="mb-4">
			<span class="flex items-center gap-2">
				<Checkbox :modelValue="project.isSingleUtilitiesChecked" :binary="true" inputId="singleUtilitiesCheckbox" @click="handleUtilitiesToggle" />

				<label for="singleUtilitiesCheckbox" class="flex flex-row items-center font-semibold cursor-pointer leading-none"
					>Single Utilities Configuration</label
				>
				<i
					class="pi pi-info-circle text-blue-500 cursor-help"
					v-tooltip.top="
						'When enabled, utilities will be shared across all steps of the single skill area. When disabled, each step will have its own separate utility configuration.'
					"
				></i>
			</span>
		</div>

		<div v-if="SessionInfo.currentUserIsAdmin()" class="mb-4">
			<span class="flex items-center gap-2">
				<Checkbox v-model="project.isTemplateProject" :binary="true" inputId="templateProjectCheckbox" />
				<label for="templateProjectCheckbox" class="flex flex-row items-center font-semibold cursor-pointer leading-none"
					>Template Project</label
				>
				<i
					class="pi pi-info-circle text-blue-500 cursor-help"
					v-tooltip.top="
						'When enabled, this project will be available as a template source when copying steps. Users can copy steps and their upstream intermediates from template projects into their own assessments.'
					"
				></i>
			</span>
		</div>

		<I2FormRow>
			<I2FormField label="Project Name">
				<InputText v-model="project.projectName" :class="{ 'p-invalid': saveAttempted && !project.projectName }" maxlength="200" /> </I2FormField
			><I2FormField label="Autocreate Sub Projects" v-if="props.project.id === undefined">
				<div class="flex justify-content-center">
					<Checkbox v-model="subProjectCreationFlag" :binary="true" /></div
			></I2FormField>
		</I2FormRow>
		<I2FormRow>
			<I2FormField label="Project Collaborators">
				<AutoComplete
					:multiple="true"
					v-model="selectedPersons"
					:suggestions="filteredPersons"
					:optionLabel="personLabel"
					optionValue="id"
					@complete="searchPerson($event)"
					:field="personLabel"
					@item-select="checkIfPersonIsPresent($event)"
				/>
			</I2FormField>
		</I2FormRow>
		<I2FormRow>
			<I2FormField label="Project Comments">
				<Textarea v-model="project.comments" rows="4" />
			</I2FormField>
		</I2FormRow>
		<I2FormRow right> </I2FormRow
	></Dialog>
</template>
<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue"
import { ApiClient, Project, Person, ProjectCollaboratorPut, FeatureFlagClient } from "../../entities/NSwagGenerated"
import { SessionInfo, PageAccess } from "../auth/personSessionData"
import Button from "primevue/button"
import Textarea from "primevue/textarea"
import Dialog from "primevue/dialog"
import AutoComplete, { AutoCompleteCompleteEvent } from "primevue/autocomplete"
import InputText from "primevue/inputtext"
import Checkbox from "primevue/checkbox"
import I2FormRow from "@/crispify/crispy-components/I2FormRow.vue"
import I2FormField from "@/crispify/crispy-components/I2FormField.vue"
import * as Session from "../auth/personSessionData"
import { useToast } from "primevue/usetoast"
import { useConfirm } from "primevue/useconfirm"
import { string } from "zod/v4"
const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const featureFlagClient = new FeatureFlagClient(import.meta.env.VITE_API_URL?.toString())
const saveAttempted = ref<boolean>(false)
const selectedPersons = ref<Person[] | undefined>([] as Person[])
const filteredPersons = ref<Person[] | undefined>([] as Person[])
const multiStepUtilitiesEnabled = ref<boolean>(false)
const project = ref(new Project())
let projectAndCollaborators = new ProjectCollaboratorPut()
const pageEditText = ref<string>("")
const toast = useToast()
const confirm = useConfirm()
const dbIsSingleUtilitiesState = ref<boolean>(false)
const isProcessingConfirmation = ref<boolean>(false)

// Function to wrap text every 25 characters
function wrapText(text: string, maxLength: number = 25): string {
	if (!text) return ""
	const words = text.split(" ")
	const lines: string[] = []
	let currentLine = ""

	for (const word of words) {
		if ((currentLine + word).length <= maxLength) {
			currentLine += (currentLine ? " " : "") + word
		} else {
			if (currentLine) {
				lines.push(currentLine)
				currentLine = word
			} else {
				// Word is longer than maxLength, break it
				let remainingWord = word
				while (remainingWord.length > maxLength) {
					lines.push(remainingWord.substring(0, maxLength))
					remainingWord = remainingWord.substring(maxLength)
				}
				currentLine = remainingWord
			}
		}
	}
	if (currentLine) {
		lines.push(currentLine)
	}
	return lines.join("\n")
}
const props = defineProps({
	showDialog: { type: Boolean, required: true },
	project: { type: Project, required: true },
	cancel: Function,
	save: Function,
})
const emit = defineEmits<{
	(e: "cancel"): void
	(e: "save"): void
	(e: "deleted"): void
}>()
const subProjectCreationFlag = ref(true)

onMounted(async () => {
	// Check feature flag first
	await checkMultiStepUtilityFeatureFlag()

	if (props.project.id === undefined) {
		pageEditText.value = "Create Project"
		project.value.id = self.crypto.randomUUID()
		project.value.isActive = true
		project.value.projectOwner = Session.SessionInfo.currentUser().id
		// Default to single step (true) when multi-step flag is off, otherwise false
		project.value.isSingleUtilitiesChecked = !multiStepUtilitiesEnabled.value
		dbIsSingleUtilitiesState.value = !multiStepUtilitiesEnabled.value
		selectedPersons.value = [] as Person[]
	} else {
		pageEditText.value = "Modify Project"
		project.value = Project.fromJS(props.project.toJSON())
		// if multi-step feature is disabled but the project does not have single utilities checked, show the checkbox
		multiStepUtilitiesEnabled.value = !project.value.isSingleUtilitiesChecked || multiStepUtilitiesEnabled.value
		// Store the initial state for comparison
		dbIsSingleUtilitiesState.value = project.value.isSingleUtilitiesChecked || false
		selectedPersons.value = addFullName(props.project.projectCollaborator)
	}
})

// Function to check if the Multi-Step Utility Data Entry feature flag is enabled
async function checkMultiStepUtilityFeatureFlag() {
	try {
		const featureFlag = await featureFlagClient.getByName("Multi-Step Utility Data Entry")
		multiStepUtilitiesEnabled.value = featureFlag.isEnabled || false
	} catch (error) {
		// If the feature flag doesn't exist or there's an error, default to false (hide the flag)
		multiStepUtilitiesEnabled.value = false
		console.warn("Feature flag 'Multi-Step Utility Data Entry' not found or error occurred:", error)
	}
}

const personLabel = (person: Person) => {
	return `${person.firstName} ${person.lastName}`
}
function checkIfPersonIsPresent(event: any) {
	if (selectedPersons.value === undefined) return
	let isPresent = selectedPersons.value.filter((person: any) => person.id === event.value.id)
	if (isPresent.length > 1) {
		toast.add({
			severity: "warn",
			summary: "The selected collaborator is already in the collaborator list for this Project",
			detail: `Select another collaborator`,
			life: 3000,
		})
		selectedPersons.value.splice(-1, 1)
	}
}

function addFullName(personList: any) {
	let personTrureList = Array<Person>()
	personList.forEach((single: any) => {
		single.person.fullName = single.person.firstName + " " + single.person.lastName
		personTrureList.push(single.person)
	})
	return personTrureList
}

// Handle utilities toggle with single confirmation
function handleUtilitiesToggle(event: Event) {
	// Prevent the default checkbox behavior
	event.preventDefault()
	event.stopPropagation()

	// Prevent multiple simultaneous confirmations
	if (isProcessingConfirmation.value) {
		console.log("Confirmation already in progress, ignoring click")
		return
	}

	const newState = !project.value.isSingleUtilitiesChecked

	// If this is the first time setting (new project) or no actual change, allow it
	if (dbIsSingleUtilitiesState.value === newState) {
		project.value.isSingleUtilitiesChecked = newState
		return
	}

	// Set flag to prevent multiple calls
	isProcessingConfirmation.value = true
	console.log("Starting confirmation for state change:", dbIsSingleUtilitiesState.value, "->", newState)

	// Prepare messages based on the state change
	let message = ""
	let header = ""

	if (!dbIsSingleUtilitiesState.value && newState) {
		// Changing from disabled to enabled
		header = "Enable Single Step Utilities Configuration"
		message = wrapText(
			"Activating this option will affect the entire project. If data was previously entered, only utility data from the first step of Synthetic API, Biologics, and Product Process will be retained. Do you want to continue?"
		)
	} else if (dbIsSingleUtilitiesState.value && !newState) {
		// Changing from enabled to disabled
		header = "Enable Multi-Step Utilities Configuration"
		message = wrapText(
			"The current utilities data will be visible in the first step of Synthetic API, Biologics, and Product Process and you will be able to enter utilities data on subsequent steps. Do you want to continue?"
		)
	}

	// Show confirmation dialog
	confirm.require({
		message: message,
		header: header,
		icon: "pi pi-exclamation-triangle",
		acceptLabel: "Yes, Continue",
		rejectLabel: "Cancel",
		acceptClass: "p-button-warning",
		rejectClass: "p-button-secondary text-white",
		accept: () => {
			console.log("User accepted change")
			// User confirmed the change
			project.value.isSingleUtilitiesChecked = newState
			// DO NOT update previousUtilitiesState here - we'll do it only after the actual save
			// Reset flag after nextTick to ensure all events are processed
			nextTick(() => {
				isProcessingConfirmation.value = false
			})
		},
		reject: () => {
			console.log("User rejected change")
			// User cancelled - keep current state
			// Reset flag after nextTick to ensure all events are processed
			nextTick(() => {
				isProcessingConfirmation.value = false
			})
		},
	})
}

function testFields() {
	if (project.value.projectName == null) {
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "You must assign a name to a project",
			life: 2500,
		})
		saveAttempted.value = true
		return false
	}
	return true
}
async function save() {
	if (!testFields()) return
	performSave()

	// Check if isSingleUtilitiesChecked has changed from its previous state
	// const utilitiesFlagChanged = dbIsSingleUtilitiesState.value !== project.value.isSingleUtilitiesChecked

	// console.log("Save clicked - Debug info:")
	// console.log("Previous state:", dbIsSingleUtilitiesState.value)
	// console.log("Current state:", project.value.isSingleUtilitiesChecked)
	// console.log("Flag changed:", utilitiesFlagChanged)
	// console.log("Project ID:", project.value.id)
	// console.log("Props project ID:", props.project.id)

	// // If utilities flag changed and we're editing an existing project, ask for confirmation
	// if (utilitiesFlagChanged && props.project.id !== undefined) {
	// 	let confirmationMessage = ""
	// 	let confirmationHeader = ""

	// 	if (project.value.isSingleUtilitiesChecked) {
	// 		// Enabling single utilities
	// 		confirmationHeader = "Enable Single Utilities Configuration"
	// 		confirmationMessage =
	// 			"Enabling this option will reset all utilities energy consumption values to defaults for this project. This will affect all existing data. Do you want to continue with saving?"
	// 	} else {
	// 		// Disabling single utilities
	// 		confirmationHeader = "Disable Single Utilities Configuration"
	// 		confirmationMessage =
	// 			"Disabling this option will reset all utilities energy consumption values to defaults for this project. This will affect all existing data. Do you want to continue with saving?"
	// 	}

	// 	// Show confirmation dialog before saving
	// 	confirm.require({
	// 		message: confirmationMessage,
	// 		header: confirmationHeader,
	// 		icon: "pi pi-exclamation-triangle",
	// 		acceptLabel: "Yes, Save and Reset",
	// 		rejectLabel: "Cancel",
	// 		acceptClass: "p-button-warning",
	// 		rejectClass: "p-button-secondary text-white",
	// 		accept: () => {
	// 			// User confirmed - proceed with save
	// 			performSave(utilitiesFlagChanged)
	// 		},
	// 		reject: () => {
	// 			// User cancelled - do nothing
	// 			console.log("User cancelled save operation")
	// 		},
	// 	})
	// } else {
	// 	// No flag change or new project - save directly
	// 	performSave(utilitiesFlagChanged)
	// }
}

async function performSave() {
	projectAndCollaborators.autoCreateSubProjects = subProjectCreationFlag.value
	projectAndCollaborators.collaborators = selectedPersons.value
	projectAndCollaborators.project = project.value

	try {
		// Save the project first
		await apiClient.projectPost(projectAndCollaborators)

		// If utilities flag changed and we're editing an existing project, reset utilities to defaults
		// if (utilitiesFlagChanged && project.value.id) {
		// 	try {
		// 		// Call the new API endpoint to reset utilities energy consumption to defaults
		// 		await apiClient.resetUtilitiesEnergyConsumptionToDefaults(project.value.id)

		// 		toast.add({
		// 			severity: "success",
		// 			summary: "Success",
		// 			detail: "Project saved and utilities energy consumption reset to defaults",
		// 			life: 3000,
		// 		})
		// 	} catch (error) {
		// 		console.error("Error resetting utilities to defaults:", error)
		// 		toast.add({
		// 			severity: "warn",
		// 			summary: "Warning",
		// 			detail: "Project saved but failed to reset utilities to defaults. Please reset them manually.",
		// 			life: 5000,
		// 		})
		// 	}
		// } else {
		// 	toast.add({
		// 		severity: "success",
		// 		summary: "Success",
		// 		detail: "Project saved successfully",
		// 		life: 3000,
		// 	})
		// }

		// Update the previous state to reflect the current state
		dbIsSingleUtilitiesState.value = project.value.isSingleUtilitiesChecked || false

		emit("save")
	} catch (error) {
		console.error("Error saving project:", error)
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Failed to save project",
			life: 3000,
		})
	}
}

function searchPerson(event: AutoCompleteCompleteEvent) {
	apiClient.filterPersonForCollaborators(event.query).then((res) => {
		filteredPersons.value = res
	})
}
</script>
<style scoped></style>
